import React from 'react';
import { useFormik } from 'formik';
import { v4 as uuid } from 'uuid';

import * as S from './styles';
import { PatientProps } from '../../contexts/PatientContext';
import { patientSchema } from '../../Schemas/patient';
import { maskCpf, maskDateStringIso, maskPassport, maskPhone, maskStringDate, removeMask } from '../../utils/masks';
import { Button, FieldsWrapper } from '../../components';
import HeadTitle from '../../components/HeadTitle';
import TagInfo from '../../components/TagInfo';
import ValidationDocument from '../../components/ValidationDocument';
import { Dropdown } from '../../components/Dropdown';
import { default as InputText } from '../../components/Input';
import { isOlderThan, onlyLetters, validateDate, validateEmail, validateName } from '../../utils/validators';
import FooterWrapper from '../../components/FooterWrapper';

const alertEinsteinIcon =
  'https://agendamento-images-nprd.telemedicinaeinstein.com.br/schedule-lib-assets/warningBlue.svg';

function formatPatientProps(patientData: any): PatientProps {
  const patient = {} as PatientProps;
  patient.name = patientData.name.trim();
  patient.email = patientData.email.trim();
  patient.idPatient = patientData.idPatient;

  patient.document = removeMask(patientData.document);
  patient.phone = removeMask(patientData.phone);

  if (!isOlderThan(patientData.birthDate, 9)) patient.pregnant = false;
  else patient.pregnant = patientData.pregnant === 'Sim';

  return { ...patientData, ...patient };
}

export const INITIAL_PATIENT_FORM_VALUES = {
  documentType: 'CPF',
  document: '',
  citizenship: '',
  gender: '',
  pregnant: '',
  name: '',
  birthDate: '',
  birthDateDate: '',
  phone: '',
  email: '',
  idPatient: ''
};

export type PatientFormPageProps = {
  patient?: PatientProps;
  handlePatientSubmit: (patient: PatientProps) => void;
  goBack?: () => void;
};

export function PatientFormTemplate({ patient, handlePatientSubmit, goBack }: PatientFormPageProps) {
  const [validDocument, setValidDocument] = React.useState(true);

  const formik = useFormik({
    initialValues: INITIAL_PATIENT_FORM_VALUES,
    validationSchema: patientSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async values => handlePatientSubmit(formatPatientProps(values))
  });

  function updateFormPatient(patient: PatientProps) {
    formik.setValues({
      ...patient,
      document: patient.documentType === 'CPF' ? maskCpf(patient.document) : maskPassport(patient.document),
      phone: maskPhone(patient.phone),
      birthDateDate: maskDateStringIso(patient.birthDate) || '',
      pregnant: patient.gender === 'Feminino' ? (patient.pregnant ? 'Sim' : 'Não') : '',
      gender: patient.gender === 'não informado' ? '' : patient.gender,
      idPatient: patient.idPatient || ''
    });
  }

  React.useEffect(() => {
    if (patient) updateFormPatient(patient);
  }, [patient]);

  function checkButtonEnable(): boolean {
    const errors = formik.errors;

    const values: { [index: string]: string } = formik.values;

    const requiredFields = ['document', 'documentType', 'name', 'email', 'birthDateDate', 'phone'];

    const hasEmptyFields = requiredFields.some(field => !values[field]);

    const hasErrors = Object.values(errors).some(error => !!error);

    return hasEmptyFields || hasErrors;
  }

  return (
    <S.Container>
      <FieldsWrapper>
        <HeadTitle title="Informe os dados do paciente" goBack={goBack} />
        <div className="info-wrapper">
          <TagInfo
            icon={<img src={alertEinsteinIcon} alt="alert icon" />}
            info="Os dados do paciente são confidenciais e de uso exclusivo dos serviços Einstein."
          />
        </div>
        <>
          <ValidationDocument
            setFieldValue={formik.setFieldValue}
            setFieldError={formik.setFieldError}
            document={formik.values.document}
            documentType={formik.values.documentType}
            error={formik.errors.document}
            setIsValid={setValidDocument}
            isValid={validDocument}
            onConfirmIdentity={() => {}}
            showConfirmIdentity={false}
          />
          <>
            <S.FieldWrapper>
              <Dropdown
                value={formik.values.citizenship}
                label="Nacionalidade"
                placeholder="Nacionalidade"
                options={['Brasileiro', 'Estrangeiro']}
                onSelect={value => {
                  formik.setFieldValue('citizenship', value);
                  formik.setFieldError('citizenship', '');
                }}
                error={formik.errors.citizenship}
              />
            </S.FieldWrapper>
            <S.FieldWrapper>
              <InputText
                name="name"
                id="name"
                placeholder="Nome"
                label="Nome"
                onChange={e => {
                  const nameOnlyLetters = onlyLetters(e.target.value);
                  formik.setFieldValue('name', nameOnlyLetters);
                  if (!validateName(nameOnlyLetters)) {
                    formik.setFieldError('name', 'Digite nome completo');
                  } else {
                    formik.setFieldError('name', '');
                  }
                }}
                value={formik.values.name}
                error={formik.errors.name}
              />
            </S.FieldWrapper>
            <S.FieldWrapper>
              <InputText
                name="birthDateDate"
                id="birthDateDate"
                label="Data de nascimento"
                placeholder="Data de nascimento"
                maxLength={10}
                onChange={e => {
                  const dateStringMasked = maskStringDate(e.currentTarget.value);
                  formik.setFieldValue('birthDateDate', dateStringMasked);
                  const dateString = validateDate(dateStringMasked);
                  if (dateStringMasked.length < 10 || !dateString) {
                    formik.setFieldError('birthDateDate', 'Data inválida');
                  } else {
                    formik.setFieldError('birthDateDate', '');
                    formik.setFieldValue('birthDate', dateString);
                  }
                }}
                value={formik.values.birthDateDate}
                error={formik.errors.birthDateDate}
              />
            </S.FieldWrapper>
            <S.FieldWrapper>
              <Dropdown
                value={formik.values.gender}
                label="Sexo"
                placeholder="Sexo"
                options={['Masculino', 'Feminino']}
                onSelect={value => {
                  formik.setFieldValue('gender', value);
                  formik.setFieldError('gender', '');
                  if (value === 'Masculino') {
                    formik.setFieldValue('pregnant', '');
                  }

                  // const validGender = validateGenderCatalogGenericRules(value);
                  // if (!validGender)
                  //   formik.setFieldError('gender', 'O tipo de procedimento escolhido é somente para o sexo feminino');
                }}
                error={formik.errors.gender}
              />
            </S.FieldWrapper>
            {formik.values.gender === 'Feminino' && isOlderThan(formik.values.birthDate, 9) && (
              <S.FieldWrapper>
                <Dropdown
                  value={formik.values.pregnant}
                  label="Gestante"
                  placeholder="É gestante?"
                  options={['Sim', 'Não']}
                  onSelect={value => {
                    formik.setFieldValue('pregnant', value);
                    formik.setFieldError('pregnant', '');
                  }}
                  error={formik.errors.pregnant}
                />
              </S.FieldWrapper>
            )}
            <S.FieldWrapper>
              <InputText
                name="email"
                placeholder="Email"
                label="Email"
                type="text"
                onChange={e => {
                  const email = e.target.value;
                  formik.setFieldValue('email', email);
                  if (!validateEmail(email)) {
                    formik.setFieldError('email', 'Digite um email válido');
                  } else {
                    formik.setFieldError('email', '');
                  }
                }}
                value={formik.values.email}
                error={formik.errors.email}
              />
            </S.FieldWrapper>
            <S.FieldWrapper>
              <InputText
                name="phone"
                placeholder="Telefone"
                label="Telefone"
                type="text"
                onChange={e => {
                  const maskedPhone = maskPhone(e.target.value);
                  formik.setFieldValue('phone', maskedPhone);
                  if (maskedPhone.length < 13) {
                    formik.setFieldError('phone', 'Telefone inválido');
                  } else {
                    formik.setFieldError('phone', '');
                  }
                }}
                value={formik.values.phone}
                error={formik.errors.phone}
              />
            </S.FieldWrapper>
          </>
        </>
      </FieldsWrapper>
      <FooterWrapper>
        <S.ButtonWrapper>
          <Button title="Continuar" type="button" disabled={checkButtonEnable()} onClick={() => formik.submitForm()} />
        </S.ButtonWrapper>
      </FooterWrapper>
    </S.Container>
  );
}
